import { Tabs } from 'antd';
import { Navigate, useNavigate } from 'react-router';
import type { DispatchAllocations, DispatchPreview } from '../dispatch.api';
import { DISPATCH_TRIP_TAB_LABELS, dispatchPaths, type DispatchTripTab } from '../dispatch.paths';
import { useDispatchPermissions } from '../hooks/use-dispatch-permissions';
import { availableTripTabs } from '../policies/dispatch-trip-tabs.policy';
import { DispatchCashTab } from './DispatchCashTab';
import { DispatchCrewCard } from './DispatchCrewCard';
import { DispatchTripOverview } from './DispatchTripOverview';
import { DriverCapturePanel } from './DriverCapturePanel';

interface DispatchTripBodyProps {
  tab: DispatchTripTab;
  preview: DispatchPreview;
  isSubmitting: boolean;
  error: string | null;
  onDispatch: (allocations: DispatchAllocations) => Promise<void>;
  onOverridden: () => void;
}

/** Everything under the header once the preview has loaded: the crew always in view, then the tabs the trip and the user have use for. */
export function DispatchTripBody({ tab, preview, isSubmitting, error, onDispatch, onOverridden }: DispatchTripBodyProps) {
  const navigate = useNavigate();
  const permissions = useDispatchPermissions();
  const driver = preview.crew.driver;
  const tabs = availableTripTabs({
    tripStatus: preview.trip.status,
    hasDriver: driver !== null,
    hasDispatched: preview.dispatch !== null,
    canDispatch: permissions.canDispatch,
    canTopUp: permissions.canTopUp,
    canViewCash: permissions.canViewCash,
  });
  if (!tabs.includes(tab)) return <Navigate to={dispatchPaths.dispatchTripTab(preview.trip.id, 'overview')} replace />;

  const content = {
    overview: (
      <DispatchTripOverview
        preview={preview}
        permissions={permissions}
        isSubmitting={isSubmitting}
        error={error}
        onDispatch={onDispatch}
        onChanged={onOverridden}
      />
    ),
    record: driver && <DriverCapturePanel tripId={preview.trip.id} driver={driver} />,
    cash: (
      <DispatchCashTab
        preview={preview}
        canTopUp={permissions.canTopUp}
        canViewCash={permissions.canViewCash}
        canRecordCash={permissions.canRecordCash}
        onChanged={onOverridden}
      />
    ),
  }[tab];

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <DispatchCrewCard preview={preview} canChange={permissions.canDispatch && preview.trip.status === 'assigned'} />
      {tabs.length === 1 ? (
        content
      ) : (
        <Tabs
          activeKey={tab}
          onChange={(key) => void navigate(dispatchPaths.dispatchTripTab(preview.trip.id, key as DispatchTripTab))}
          items={tabs.map((key) => ({
            key,
            label: DISPATCH_TRIP_TAB_LABELS[key],
            children: key === tab ? <div className="flex flex-col gap-4">{content}</div> : null,
          }))}
        />
      )}
    </div>
  );
}
